import {
  ChangeDetectionStrategy,
  Component,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { FormControlDynamicComponent } from '@form';
import { TranslocoDirective } from '@jsverse/transloco';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs';
import { effect } from '@angular/core';

@Component({
  selector: 'ui-filters-search',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormControlDynamicComponent,
    ReactiveFormsModule,
    TranslocoDirective,
  ],
  template: `
    <ng-container *transloco="let t">
      <form [formGroup]="form" class="w-full sm:w-60">
        <form-control
          [control]="searchControl"
          [label]="t('FILTERS.SEARCH.LABEL')"
          [type]="'string'"
          [value]="value()"
          [typeInputs]="{
            suffixIcon: 'search',
          }"
        />
      </form>
    </ng-container>
  `,
})
export class FilterSearchComponent {
  protected readonly SEARCH_DEBOUNCE_TIME: number = 500; // ms
  protected readonly SEARCH_MIN_CHARS: number = 3;
  protected readonly SEARCH_INIT_VALUE: string = '';
  value = signal<string>(this.SEARCH_INIT_VALUE)
  private fb = inject(FormBuilder);
  centerId = input<string>('');
  disabled = input<boolean>(false);
  onSearch = output<string>();

  searchControl: FormControl = this.fb.control('');
  form: FormGroup = this.fb.group({ search: this.searchControl });

  constructor() {
    this.searchControl.valueChanges
      .pipe(
        takeUntilDestroyed(),
        debounceTime(this.SEARCH_DEBOUNCE_TIME),
        distinctUntilChanged(),
        filter(
          (value) => value.length > this.SEARCH_MIN_CHARS || value.length === 0,
        ),
      )
      .subscribe((value) => {
        this.onSearch.emit(value);
      });

    // react to centerId changes
    effect(()=>{
      const centerId = this.centerId();
      this.form.setValue({ search: centerId });
      this.value.set(centerId);
      this.onSearch.emit(centerId);
    });

    // react to disabled input changes
    effect(()=>{
      if (this.disabled()) {
        this.form.disable({ emitEvent: false });
      } else {
        this.form.enable({ emitEvent: false });
      }
    });
  }
}
